import '@babylonjs/core/Meshes/thinInstanceMesh';
import type { ArenaDef } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

/** Layers of mist per bank, and how high each lies over the floor, in metres. */
export const FOG_LAYERS = [0.25, 0.8, 1.5] as const;
/** How thick each layer is at a bank's heart: the lowest thickest, the ones above thinning out. */
const DENSITY = [0.5, 0.36, 0.22] as const;
/** The mist's colour by day, and by moonlight, where a white mist would glow. */
const DAY_MIST = new Color3(0.86, 0.9, 0.94);
const NIGHT_MIST = new Color3(0.3, 0.34, 0.45);
const SIZE = 64;

/**
 * How thick a bank is at this distance from its centre, as a share of its radius: whole out to
 * 70 %, then fading to nothing at the rim, `smoothstep(0.7 r, r)` (doc 12), so banks can be
 * scattered and overlap without seams.
 */
export function fogDensity(distance: number): number {
  if (!(distance < 1)) return 0;
  if (distance <= 0.7) return 1;
  const t = (distance - 0.7) / 0.3;
  return 1 - t * t * (3 - 2 * t);
}

/** A white disc whose alpha follows `fogDensity` out to the texture's edge. */
function fogTexture(scene: Scene): Texture {
  const data = new Uint8Array(SIZE * SIZE * 4);
  for (let y = 0; y < SIZE; y++)
    for (let x = 0; x < SIZE; x++) {
      const dx = (x + 0.5) / SIZE - 0.5;
      const dy = (y + 0.5) / SIZE - 0.5;
      const i = (y * SIZE + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(255 * fogDensity(Math.hypot(dx, dy) / 0.5));
    }
  const texture = RawTexture.CreateRGBATexture(
    data,
    SIZE,
    SIZE,
    scene,
    true,
    false,
    Texture.TRILINEAR_SAMPLINGMODE,
  );
  texture.name = 'arenaFog';
  texture.hasAlpha = true;
  return texture;
}

/**
 * The arena's fog banks (content:quality#2, x-aaa-gap#10), which were authored and validated and
 * drawn by nothing. Each bank is a few flat layers of mist over the floor, the thickest lowest,
 * all of them thin instances of one quad: one draw call however many banks. Unlit, never
 * shadowed, and visual only, as doc 12 has it; the layers turn slowly against each other, so the
 * mist drifts.
 */
export class ArenaFog {
  private readonly mesh: Mesh | null;
  private readonly material: StandardMaterial | null;
  private readonly banks: { x: number; z: number; radius: number }[];
  private readonly matrices: Float32Array;
  private readonly colors: Float32Array;
  private readonly scale = new Vector3();
  private readonly turn = new Quaternion();
  private readonly at = new Vector3();
  private readonly matrix = new Matrix();
  private clock = 0;

  constructor(scene: Scene, parent: TransformNode, fog: ArenaDef['fog']) {
    this.banks = (fog ?? []).map(({ at, radius }) => ({
      x: at[0] + 0.5,
      z: at[1] + 0.5,
      radius,
    }));
    const count = this.banks.length * FOG_LAYERS.length;
    this.matrices = new Float32Array(count * 16);
    this.colors = new Float32Array(count * 4);
    if (count === 0) {
      this.mesh = null;
      this.material = null;
      return;
    }
    this.mesh = MeshBuilder.CreateGround('arenaFog', { width: 2, height: 2 }, scene);
    this.mesh.parent = parent;
    this.mesh.isPickable = false;
    // The fixed camera sees the whole arena; the base quad's own box would cull every layer.
    this.mesh.alwaysSelectAsActiveMesh = true;
    const material = new StandardMaterial('arenaFog', scene);
    material.disableLighting = true;
    material.emissiveColor = DAY_MIST.clone();
    // Each layer's thickness rides in its instance colour's alpha, over the disc's own fade.
    material.opacityTexture = fogTexture(scene);
    material.backFaceCulling = false;
    // Mist veils what is behind it and hides nothing from the depth test.
    material.disableDepthWrite = true;
    this.material = material;
    this.mesh.material = material;
    this.banks.forEach((_, b) =>
      FOG_LAYERS.forEach((_, l) => {
        this.colors.set([1, 1, 1, DENSITY[l]!], (b * FOG_LAYERS.length + l) * 4);
      }),
    );
    this.write();
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    this.mesh.thinInstanceSetBuffer('color', this.colors, 4, true);
  }

  /** How many layers of mist are drawn: every bank's. */
  get layers(): number {
    return this.mesh ? this.mesh.thinInstanceCount : 0;
  }

  /** Tints the mist for the square's light: 1 at noon, 0 by moonlight. */
  setLight(brightness: number): void {
    this.material?.emissiveColor.copyFrom(
      Color3.Lerp(NIGHT_MIST, DAY_MIST, Math.min(1, Math.max(0, brightness))),
    );
  }

  /** Call once per rendered frame, with the duel's shown time: the layers turn. */
  update(dt: number): void {
    if (!this.mesh) return;
    this.clock += dt;
    this.write();
    this.mesh.thinInstanceBufferUpdated('matrix');
  }

  dispose(): void {
    this.material?.opacityTexture?.dispose();
    this.material?.dispose();
    this.mesh?.dispose();
  }

  private write(): void {
    this.banks.forEach((bank, b) => {
      FOG_LAYERS.forEach((height, l) => {
        // Neighbouring layers turn opposite ways, each bank and layer from its own angle.
        const spin = (l % 2 === 0 ? 1 : -1) * this.clock * 0.02 + b + l;
        this.scale.set(bank.radius, 1, bank.radius);
        Quaternion.RotationYawPitchRollToRef(spin, 0, 0, this.turn);
        this.at.set(bank.x, height, bank.z);
        Matrix.ComposeToRef(this.scale, this.turn, this.at, this.matrix);
        this.matrix.copyToArray(this.matrices, (b * FOG_LAYERS.length + l) * 16);
      });
    });
  }
}
